import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import BillListTable from "../../../components/BillListTable";
import { useHistory } from "react-router";
import InputField from "../../../FormComponents/InputField";
import SaleAddBill from "./SaleAddBill";
import {
  getSaleBillData,
  deleteSaleBillById,
  sendSaleBillToCustomer,
} from "../../../services/Sales";
import Ex_ProductSelectingPopup from "../../../components/Ex_ProductSelectingPopup";
import CustomerSelectingPopup from "../../../components/CustomerSelectingPopup";
import ConfirmationPopup from "../../../components/ConfirmationPopup";
import FormAlert from "../../../components/FormAlert";

// Import icons
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import FilterListIcon from "@mui/icons-material/FilterList";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ReceiptIcon from "@mui/icons-material/Receipt";
import CategoryIcon from "@mui/icons-material/Category";
import PersonIcon from "@mui/icons-material/Person";
import StorefrontIcon from "@mui/icons-material/Storefront";
import BusinessIcon from "@mui/icons-material/Business";
import { RoleBasedRender } from "../../../components/RoleBaseRender";

// Custom styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
}));

const HeaderCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.shape.borderRadius * 1.5,
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(3),
  boxShadow: "0 8px 16px 0 rgba(0,0,0,0.1)",
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 1.5,
  textTransform: "none",
  fontWeight: 600,
  boxShadow: "0 4px 12px 0 rgba(0,0,0,0.1)",
}));

const FilterContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 1.5,
  backgroundColor: alpha(theme.palette.primary.light, 0.05),
  marginBottom: theme.spacing(3),
}));

const PaginationContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

const SelectButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  textTransform: "none",
  minWidth: "100%",
  justifyContent: "flex-start",
  padding: theme.spacing(1),
  backgroundColor: alpha(theme.palette.primary.light, 0.1),
  color: theme.palette.text.primary,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.light, 0.2),
    borderColor: theme.palette.primary.main,
  },
}));

const billStatus = [
  { value: "DRAFT", label: "Draft" },
  { value: "REJECTED", label: "Rejected" },
  { value: "APPROVED", label: "Approved" },
  { value: "AWAITING", label: "Awaiting Approval" },
  { value: "PENDING", label: "Pending" },
  { value: "SETTLED", label: "Settled" },
];

const billCategories = [
  { value: "", label: "All Categories" },
  { value: "RETAIL", label: "Retail" },
  { value: "WHOLESALE", label: "Wholesale" },
];

let _saleBillListState = null;

function SaleBills() {
  const history = useHistory();

  // State management
  const [rows, setRows] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openForm, setOpenForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Selected products and customers
  const [selectedProduct, setSelectedProduct] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState([]);

  // Popup states
  const [openProductSelection, setOpenProductSelection] = useState(false);
  const [openCustomerSelection, setOpenCustomerSelection] = useState(false);
  const [openPopup, setOpenPopup] = useState(false);

  // Confirmation and alert states
  const [isMatched, setIsMatched] = useState(false);
  const [matchingText, setMachingText] = useState("");
  const [confirmationText, setConfirmationText] = useState("");
  const [deletingRow, setDeletingRow] = useState("");

  const [displayAlert, setAlert] = useState(false);
  const [alertData, setAlertData] = useState({
    type: "",
    message: "",
  });

  // Filter state
  const [filter, setFilter] = useState({
    _billNumber: "",
    _products: [],
    _barcode: "",
    _addedBy: "",
    _customer: [],
    _status: "",
    _billCategory: "", // NEW: Bill category filter
  });

  // Table columns configuration
  const columns = [
    {
      id: "date",
      label: "Date",
      minWidth: 100,
    },
    {
      id: "billNumber",
      label: "Bill Number",
      minWidth: 120,
      format: (value) => (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <ReceiptIcon sx={{ fontSize: 16, mr: 1, color: "primary.main" }} />
          {value}
        </Box>
      ),
    },
    {
      id: "billCategory",
      label: "Category",
      minWidth: 110,
      format: (value) => (
        <Chip
          icon={value === "RETAIL" ? <StorefrontIcon /> : <BusinessIcon />}
          label={value}
          size="small"
          color={value === "RETAIL" ? "primary" : "secondary"}
          variant="outlined"
        />
      ),
    },
    {
      id: "customer",
      label: "Customer",
      minWidth: 150,
      format: (value) => (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <PersonIcon sx={{ fontSize: 16, mr: 1, color: "primary.main" }} />
          {value}
        </Box>
      ),
    },
    {
      id: "status",
      label: "Status",
      minWidth: 130,
      format: (value, row) => {
        let color = "default";
        let variant = "outlined";

        // Show draft indicator
        if (row.isDraft) {
          return (
            <Stack direction="row" spacing={0.5}>
              <Chip
                label="DRAFT"
                color="warning"
                size="small"
                variant="filled"
              />
              <Chip
                label={value}
                color="default"
                size="small"
                variant="outlined"
              />
            </Stack>
          );
        }

        switch (value) {
          case "APPROVED":
            color = "success";
            variant = "filled";
            break;
          case "REJECTED":
            color = "error";
            break;
          case "AWAITING":
            color = "warning";
            break;
          case "PENDING":
            color = "info";
            break;
          case "SETTLED":
            color = "primary";
            variant = "filled";
            break;
          default:
            color = "default";
        }
        return (
          <Chip label={value} color={color} size="small" variant={variant} />
        );
      },
    },
    {
      id: "totalItems",
      label: "Items",
      minWidth: 80,
      format: (value) => (
        <Chip label={value} size="small" color="default" variant="outlined" />
      ),
    },
    {
      id: "totalProfit",
      label: "Total Profit",
      minWidth: 120,
      format: (value) => (
        <Typography
          variant="body2"
          fontWeight="600"
          color={value >= 0 ? "success.main" : "error.main"}
        >
          Rs.{" "}
          {value.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </Typography>
      ),
    },
    {
      id: "totalAmount",
      label: "Total Amount",
      minWidth: 120,
      format: (value) => (
        <Typography variant="body2" fontWeight="600">
          Rs.{" "}
          {value.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </Typography>
      ),
    },
    { id: "action", label: "Actions", minWidth: 150 },
  ];

  // Initial data load
  useEffect(() => {
    if (_saleBillListState) {
      const { page: savedPage, filter: savedFilter } = _saleBillListState;
      _saleBillListState = null;
      setPage(savedPage);
      setFilter(savedFilter);
      fetchData(savedFilter, savedPage);
    } else {
      fetchData(filter, page);
    }
  }, []);

  // Effect for handling deletion confirmation
  useEffect(() => {
    const handleDeletion = async () => {
      if (isMatched && !openPopup && deletingRow != null) {
        setLoading(true);
        let deleteResponse = await deleteSaleBillById(deletingRow.action);
        if (deleteResponse) {
          fetchData(filter, page);
          setAlertData({
            type: "success",
            message: `Bill ${deletingRow.billNumber} was successfully deleted`,
          });
          setAlert(true);
        } else {
          setAlertData({
            type: "error",
            message: `Failed to delete ${deletingRow.billNumber} Bill. Something went wrong`,
          });
          setAlert(true);
        }
        setLoading(false);
      }
    };

    handleDeletion();
  }, [isMatched, openPopup]);

  // Close alert function
  const closeAlert = () => {
    setAlert(false);
    setAlertData({
      type: "",
      message: "",
    });
  };

  // Fetch data from API
  const fetchData = async (productFilter, pageNo) => {
    setLoading(true);
    try {
      console.log("Fetching sale bills with filter:", productFilter);

      let result = await getSaleBillData(pageNo, rowsPerPage, productFilter);

      console.log("API Response:", result);

      // The response is now Page<SaleBillResponseBody>
      let saleBillList = result.content;

      // Set total pages and rows for pagination
      setTotalPages(result.totalPages);
      setTotalRows(result.totalElements);

      const newSet = [];
      if (saleBillList && saleBillList.length > 0) {
        for (let x = 0; x < saleBillList.length; x++) {
          const bill = saleBillList[x];

          newSet.push(
            createData(
              bill.sellingDate,
              bill.billNumber,
              bill.billCategory, // NEW: Bill category
              bill.customerName || "N/A",
              bill.status,
              bill.isDraft, // NEW: Draft status
              bill.totalItems || 0, // NEW: Total items count
              bill.totalProfit || 0,
              bill.totalPrice || 0,
              bill.billId,
            ),
          );
        }
      }

      console.log("Processed rows:", newSet);

      // Set rows to table
      setRows(newSet);
    } catch (error) {
      console.error("Error fetching sale bills:", error);
      setAlertData({
        type: "error",
        message: "Failed to fetch sale bills data.",
      });
      setAlert(true);
    } finally {
      setLoading(false);
    }
  };

  // Create data for table
  function createData(
    date,
    billNumber,
    billCategory,
    customer,
    status,
    isDraft,
    totalItems,
    totalProfit,
    totalAmount,
    action,
  ) {
    return {
      date,
      billNumber,
      billCategory,
      customer,
      status,
      isDraft,
      totalItems,
      totalProfit,
      totalAmount,
      action,
    };
  }

  // Pagination handler
  const handleChange = async (event, value) => {
    setPage(value - 1);
    await fetchData(filter, value - 1);
  };

  // Handle action for table actions
  const handleAction = async (event, id) => {
    console.log("action", event, id);
    switch (event) {
      case "view":
        _saleBillListState = { page, filter };
        history.push("/template/sale_bill_view/" + id);
        break;
      case "production":
        history.push("/template/sale_bill_edit/" + id);
        break;
      case "disable":
        setIsMatched(false);
        const filterData = rows.find((row) => row.action === id);
        setDeletingRow(filterData);
        const topicText = `Please Confirm Bill Number To Delete ${filterData.billNumber}`;
        setConfirmationText(topicText);
        setMachingText(filterData.billNumber);
        setOpenPopup(true);
        break;
      case "saleProduct":
        handleBillSendToCustomer(id);
        break;
      default:
        return;
    }
  };

  // Handle bill send action
  const handleBillSendToCustomer = async (billId) => {
    console.log("Sending bill to customer:", billId);
    setLoading(true);
    try {
      let result = await sendSaleBillToCustomer(billId);
      if (result !== "Something went wrong...") {
        setAlertData({
          type: "success",
          message: "Sent the sale bill successfully",
        });
      } else {
        setAlertData({
          type: "error",
          message: "Something went wrong during bill sending to the customer",
        });
      }
      setAlert(true);
    } catch (error) {
      console.error("Error transferring bill items:", error);
      setAlertData({
        type: "error",
        message: "Error transferring bill items",
      });
      setAlert(true);
    } finally {
      setLoading(false);
    }
  };

  // Toggle filter visibility
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  // Reset filters
  const resetFilters = () => {
    setFilter({
      _billNumber: "",
      _products: [],
      _barcode: "",
      _addedBy: "",
      _customer: [],
      _status: "",
      _billCategory: "",
    });

    setSelectedProduct([]);
    setSelectedCustomer([]);
    closeAlert();
  };

  // Handle filter change
  const handleFilterChange = (name, val) => {
    setFilter({
      ...filter,
      [name]: val,
    });
  };

  // Popup handlers
  const handleOpenPopup = () => {
    setOpenPopup(!openPopup);
  };

  const handleOpenProductTable = () => {
    setOpenProductSelection(!openProductSelection);
    if (openProductSelection) {
      setFilter({
        ...filter,
        _products: selectedProduct,
      });
    }
  };

  const handleOpenCustomerSelection = () => {
    setOpenCustomerSelection(!openCustomerSelection);
    if (openCustomerSelection) {
      setFilter({
        ...filter,
        _customer: selectedCustomer,
      });
    }
  };

  return (
    <>
      <StyledPaper elevation={0}>
        {openForm ? (
          <SaleAddBill setOpenForm={setOpenForm} />
        ) : (
          <>
            <HeaderCard>
              <Grid container alignItems="center" spacing={2}>
                <Grid item>
                  <ReceiptIcon fontSize="large" />
                </Grid>
                <Grid item xs>
                  <Typography variant="h5" fontWeight="bold">
                    Sale Bills
                  </Typography>
                  <Typography variant="body2">
                    View and manage sale bills and transactions
                  </Typography>
                </Grid>
                <Grid item>
                  <RoleBasedRender
                    allowedRoles={["SALE_BILL_CREATE", "ROLE_ADMIN"]}
                  >
                    <ActionButton
                      variant="contained"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={() => setOpenForm(true)}
                    >
                      Add Bill
                    </ActionButton>
                  </RoleBasedRender>
                </Grid>
              </Grid>
            </HeaderCard>

            <Box
              mb={3}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <TextField
                placeholder="Search by bill number..."
                variant="outlined"
                size="small"
                sx={{ width: "40%" }}
                value={filter._billNumber}
                onChange={(e) => {
                  handleFilterChange("_billNumber", e.target.value);
                }}
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ color: "action.active", mr: 1 }} />
                  ),
                }}
              />
              <Stack direction="row" spacing={2}>
                <Tooltip title="Toggle Filters">
                  <ActionButton
                    variant="outlined"
                    color="primary"
                    startIcon={<FilterListIcon />}
                    onClick={toggleFilters}
                  >
                    Filters
                  </ActionButton>
                </Tooltip>
                <Chip
                  label={`${totalRows} Bills`}
                  variant="outlined"
                  color="primary"
                />
              </Stack>
            </Box>

            {showFilters && (
              <FilterContainer>
                <Typography variant="subtitle2" fontWeight="bold" mb={2}>
                  Advanced Filters
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <InputField
                      name="_addedBy"
                      value={filter._addedBy}
                      onChange={(event, newInputValue) =>
                        handleFilterChange(event, newInputValue)
                      }
                      type="text"
                      label="Added By"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Bill Category"
                      name="_billCategory"
                      value={filter._billCategory}
                      onChange={(event) =>
                        handleFilterChange(
                          event.target.name,
                          event.target.value,
                        )
                      }
                    >
                      {billCategories.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Status"
                      name="_status"
                      value={filter._status}
                      onChange={(event) =>
                        handleFilterChange(
                          event.target.name,
                          event.target.value,
                        )
                      }
                    >
                      <MenuItem value="">All Statuses</MenuItem>
                      {billStatus.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <SelectButton
                      startIcon={<CategoryIcon />}
                      onClick={handleOpenProductTable}
                      endIcon={
                        selectedProduct.length > 0 ? (
                          <Chip size="small" label={selectedProduct.length} />
                        ) : null
                      }
                    >
                      {selectedProduct.length > 0
                        ? "Products Selected"
                        : "Select Products"}
                    </SelectButton>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <SelectButton
                      startIcon={<PersonIcon />}
                      onClick={handleOpenCustomerSelection}
                      endIcon={
                        selectedCustomer.length > 0 ? (
                          <Chip size="small" label={selectedCustomer.length} />
                        ) : null
                      }
                    >
                      {selectedCustomer.length > 0
                        ? "Customer Selected"
                        : "Select Customer"}
                    </SelectButton>
                  </Grid>
                </Grid>
                <Box mt={2} display="flex" justifyContent="flex-end">
                  <Stack direction="row" spacing={2}>
                    <Tooltip title="Reset Filters">
                      <ActionButton
                        variant="outlined"
                        color="secondary"
                        startIcon={<RestartAltIcon />}
                        onClick={resetFilters}
                      >
                        Reset
                      </ActionButton>
                    </Tooltip>
                    <Tooltip title="Apply Filters">
                      <ActionButton
                        variant="contained"
                        color="primary"
                        onClick={() => {
                          fetchData(filter, page);
                          closeAlert();
                        }}
                      >
                        Apply Filters
                      </ActionButton>
                    </Tooltip>
                  </Stack>
                </Box>
              </FilterContainer>
            )}

            {displayAlert && (
              <Box mb={3}>
                <FormAlert type={alertData.type} message={alertData.message} />
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
                <CircularProgress />
              </Box>
            ) : rows.length === 0 ? (
              <Box sx={{ textAlign: "center", p: 5 }}>
                <Typography variant="body1" color="textSecondary">
                  No sale bills found. Try adjusting your filters or add a new
                  bill.
                </Typography>
              </Box>
            ) : (
              <BillListTable
                columns={columns}
                rows={rows}
                page={page}
                setPage={setPage}
                tablePagin={false}
                totalPages={totalPages}
                handleAction={handleAction}
                showActions={{
                  view: "Bill view",
                  production: "Add payment",
                  disable: "Delete bill",
                  saleProduct: "Send Bill",
                }}
                actionRoles={{
                  view: ["SALE_BILL_VIEW", "ROLE_ADMIN"],
                  production: ["SALE_BILL_PAYMENT_UPDATE", "ROLE_ADMIN"],
                  disable: ["SALE_BILL_DELETE", "ROLE_ADMIN"],
                  saleProduct: ["SEND_SALE_BILL", "ROLE_ADMIN"],
                }}
                conditions={{
                  production: (row) => row.status === "APPROVED",
                }}
                tableProps={{
                  sx: {
                    "& .MuiTableRow-root:hover": {
                      backgroundColor: alpha("#000", 0.04),
                    },
                  },
                }}
              />
            )}

            {totalPages > 1 && (
              <PaginationContainer>
                <Pagination
                  count={totalPages}
                  page={page + 1}
                  variant="outlined"
                  shape="rounded"
                  color="primary"
                  onChange={handleChange}
                  size="medium"
                />
              </PaginationContainer>
            )}

            {/* Popups */}
            {openPopup && (
              <ConfirmationPopup
                setOpenPopup={setOpenPopup}
                openPopup={openPopup}
                handleOpenPopup={handleOpenPopup}
                matchingText={matchingText}
                message={confirmationText}
                isMatched={isMatched}
                setIsMatched={setIsMatched}
              />
            )}

            {openCustomerSelection && (
              <CustomerSelectingPopup
                setOpenCustomerTable={setOpenCustomerSelection}
                openCustomerTable={openCustomerSelection}
                handleCloseCustomerTable={handleOpenCustomerSelection}
                setSelectedCustomer={setSelectedCustomer}
                selectedCustomer={selectedCustomer}
                isOneChoise={true}
              />
            )}

            {openProductSelection && (
              <Ex_ProductSelectingPopup
                setOpenProductTable={setOpenProductSelection}
                openProductTable={openProductSelection}
                handleCloseProductTable={handleOpenProductTable}
                setSelectedProduct={setSelectedProduct}
                selectedProduct={selectedProduct} // FIXED
                isOneChoise={false}
              />
            )}
          </>
        )}
      </StyledPaper>
    </>
  );
}

export default SaleBills;
